<script setup>
import { reactive, ref } from 'vue';
import api from '../../api.js';

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const toLargeFileError = ref(false);
const report = reactive({
    name: '',
    email: '',
    scamLink: '',
    message: ''
});

const isSubmitting = ref(false);
const attachedImages = ref([]);
const previews = ref([]);

const removePreview = (idx) => {
    URL.revokeObjectURL(previews.value[idx]);
    previews.value.splice(idx, 1);
    attachedImages.value.splice(idx, 1);
};

function formatFilename(name) {
    if (!name) return 'Bild';

    const maxLength = 20;

    if (name.length <= maxLength) return name;

    const lastDot = name.lastIndexOf('.');
    const ext = lastDot !== -1 ? name.slice(lastDot) : '';
    const baseLength = maxLength - ext.length - 3;
    const base = baseLength > 0 ? name.slice(0, baseLength) : '';

    return `${base}...${ext}`;
}

function handleFileChange(event) {
    const files = Array.from(event.target.files);

    // keep only files ≤ MAX_FILE_SIZE
    const validFiles = files.filter((file) => {
        if (file.size > MAX_FILE_SIZE) {
            toLargeFileError.value = true;
            setTimeout(() => {
                toLargeFileError.value = false;
            }, 10000);

            return false;
        }

        return true;
    });

    previews.value.forEach(URL.revokeObjectURL);

    attachedImages.value = validFiles;
    previews.value = validFiles.map((file) => URL.createObjectURL(file));
}

async function handleReport() {
    if (isSubmitting.value) return;

    isSubmitting.value = true;
    try {
        const formData = new FormData();
        formData.append('name', report.name);
        formData.append('email', report.email);
        formData.append('scamLink', report.scamLink);
        formData.append('message', report.message);

        if (attachedImages.value.length > 0) {
            formData.append('attachedFile', attachedImages.value[0]);
        }

        await api.post('/api/v1/antiscam/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });

        report.name = '';
        report.email = '';
        report.scamLink = '';
        report.message = '';
        previews.value.forEach((url) => URL.revokeObjectURL(url));
        previews.value = [];
        attachedImages.value = [];
        const input = document.getElementById('file-upload');

        if (input) input.value = '';
    } catch (error) {
        console.error('Error uploading report:', error);
    } finally {
        isSubmitting.value = false;
    }
}
</script>

<template>
    <section class="section-white container mx-auto max-w-5xl mb-12">
        <div class="flex flex-col md:flex-row md:space-x-8">
            <div class="md:w-1/2 mb-8 md:mb-0 flex flex-col justify-center">
                <h2 class="text-2xl font-bold uppercase mb-3">{{ $t('reportFakeContent.title') }}</h2>

                <div class="leading-relaxed rounded">
                    <p v-html="$t('reportFakeContent.description')"></p>

                    <div class="flex items-center mt-4">
                        <i class="fi fi-rs-envelope mr-2"></i>
                        <a href="mailto:stoppdenscam@hkcmanagement.de" class="text-blue-600 hover:underline">
                            {{ $t('reportFakeContent.emailHeading') }}
                        </a>
                    </div>
                </div>
            </div>
            <div class="md:w-1/2">
                <form class="bg-gray-50 rounded-lg p-6 shadow space-y-4" @submit.prevent="handleReport">
                    <input
                        v-model="report.name"
                        type="text"
                        :placeholder="$t('reportFakeContent.includeItem2')"
                        required
                        class="w-full p-3 border border-blue-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                    />
                    <input
                        v-model="report.email"
                        type="email"
                        :placeholder="$t('reportFakeContent.includeItem3')"
                        required
                        class="w-full p-3 border border-blue-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                    />
                    <input
                        v-model="report.scamLink"
                        type="text"
                        :placeholder="$t('reportFakeContent.includeItem1')"
                        required
                        class="w-full p-3 border border-blue-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                    />
                    <textarea
                        v-model="report.message"
                        rows="4"
                        :placeholder="$t('reportFakeContent.includeOptional')"
                        required
                        class="w-full p-3 border border-blue-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                    ></textarea>
                    <div v-if="previews.length" class="mt-4 flex flex-wrap gap-3">
                        <div v-for="(src, i) in previews" :key="i" class="relative w-20 h-24 flex flex-col items-center justify-start">
                            <img :src="src" class="w-16 h-16 object-cover rounded-lg border shadow-sm" />
                            <span class="text-xs mt-1 text-center max-w-[80px] break-words leading-tight" :title="attachedImages[i]?.name">
                                {{ formatFilename(attachedImages[i]?.name || 'Bild') }}
                            </span>
                            <button
                                type="button"
                                class="absolute top-0 right-0 bg-red hover:bg-red text-white rounded-full w-7 h-7 flex items-center justify-center text-xl"
                                @click="removePreview(i)"
                            >
                                ×
                            </button>
                        </div>
                    </div>
                    <transition
                        enter-active-class="transition-opacity duration-500"
                        leave-active-class="transition-opacity duration-500"
                        enter-from-class="opacity-0"
                        enter-to-class="opacity-100"
                        leave-from-class="opacity-100"
                        leave-to-class="opacity-0"
                    >
                        <span
                            v-if="toLargeFileError"
                            class="flex items-center bg-dashboard-lightRed border border-red text-red text-sm font-semibold px-4 py-2 rounded-md space-x-2"
                        >
                            <i class="fi fi-ss-triangle-warning text-dashboard-lightRed text-xl mr-2"></i>
                            <span>{{ $t('reportFakeContent.fileTooLarge') }}</span>
                        </span>
                    </transition>
                    <div class="flex items-center space-x-2 mt-4">
                        <label
                            for="file-upload"
                            class="relative bg-white border border-blue-200 rounded w-10 h-10 flex items-center justify-center shadow transition cursor-pointer"
                            @click="toLargeFileError = false"
                        >
                            <i class="fi fi-rr-clip text-md leading-none"></i>
                        </label>
                        <input
                            id="file-upload"
                            :disabled="isSubmitting"
                            :class="isSubmitting ? 'bg-dashboard-primary text-gray-600 cursor-not-allowed' : ''"
                            type="file"
                            accept=".jpg,.jpeg,.png"
                            multiple
                            class="sr-only"
                            @change="handleFileChange"
                        />
                        <button
                            :disabled="isSubmitting"
                            :class="isSubmitting ? 'bg-dashboard-primary text-gray-600 cursor-not-allowed' : ''"
                            type="submit"
                            class="flex-1 bg-red text-white font-semibold h-10 rounded flex items-center justify-center shadow transition cursor-pointer"
                        >
                            <i class="fi fi-bs-paper-plane text-md leading-none mr-2"></i>
                            <span>{{ $t('reportFakeContent.buttonLabel') }}</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </section>
</template>
